import { useEffect } from "react";

/**
 * Links every form label to its field (BUG-23).
 *
 * Most labels in the app are written as a <Label> placed just before an input, select or textarea, without
 * htmlFor/id. Screen readers then read the field with no name, and clicking the label does not focus the field.
 * Rather than edit hundreds of forms one by one, this finds each unlinked label and points it at the first field
 * after it, inside the same container, that has no label yet. A label with no clear field of its own is left alone,
 * because linking the wrong field is worse than not linking. It runs once and again whenever the page changes (dialogs, new rows).
 */
const FIELD = 'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([type="file"]), textarea, select, button[role="combobox"]';
let counter = 0;

/** The first free field after this label, inside the same container, and before the next label there. */
function fieldFor(label, scope) {
  const labels = [...scope.querySelectorAll("label")];
  const next = labels[labels.indexOf(label) + 1];
  return [...scope.querySelectorAll(FIELD)].find(
    (field) =>
      label.compareDocumentPosition(field) & Node.DOCUMENT_POSITION_FOLLOWING &&
      (!next || next.compareDocumentPosition(field) & Node.DOCUMENT_POSITION_PRECEDING) &&
      !(field.labels && field.labels.length) &&
      !label.contains(field)
  );
}

export function linkLabels(root = document) {
  root.querySelectorAll("label:not([for])").forEach((label) => {
    if (label.querySelector(FIELD)) return; // it wraps its own field already
    const parent = label.parentElement;
    if (!parent) return;
    // Only an unambiguous match is made: a label with no clear field of its own stays as it was.
    const field = fieldFor(label, parent);
    if (!field) return;
    if (!field.id) field.id = `auto-field-${++counter}`;
    label.htmlFor = field.id;
  });
}

export function LabelLinker() {
  useEffect(() => {
    let frame = 0;
    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => { frame = 0; linkLabels(document); });
    };
    linkLabels(document);
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => { observer.disconnect(); if (frame) cancelAnimationFrame(frame); };
  }, []);
  return null;
}
